import { Pagination } from 'react-headless-pagination'
import { FiChevronLeft, FiChevronRight, FiChevronsLeft, FiChevronsRight } from 'react-icons/fi'

type PatientPaginationProps = {
  currentPage: number
  setCurrentPage: (page: number) => void
  totalItems: number
  pageSize: number
  onPageSizeChange?: (size: number) => void
  pageSizeOptions?: number[]
  label?: string
}

export const PatientPagination = ({
  currentPage,
  setCurrentPage,
  totalItems,
  pageSize,
  onPageSizeChange,
  pageSizeOptions = [10, 20, 30, 50],
  label = 'Rows per page:'
}: PatientPaginationProps) => {
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize))
  const start = totalItems === 0 ? 0 : currentPage * pageSize + 1
  const end = Math.min((currentPage + 1) * pageSize, totalItems)

  return (
    <div className="flex flex-col gap-3 pt-4 md:flex-row md:items-center md:justify-between">
      <div className="flex items-center gap-2 text-sm text-[#4B5563]">
        <span>{label}</span>
        <select
          className="h-9 min-w-[72px] rounded-md border border-[#E5E7EB] bg-white px-2 text-sm outline-none transition-colors focus:border-[#D1D5DB]"
          onChange={e => {
            onPageSizeChange?.(Number(e.target.value))
          }}
          value={pageSize}
        >
          {pageSizeOptions.map(size => (
            <option key={size} value={size}>
              {size}
            </option>
          ))}
        </select>
      </div>

      <div className="flex items-center gap-3 text-sm text-[#6B7280]">
        <span>
          {start} - {end} of {totalItems}
        </span>

        <Pagination
          className="flex items-center gap-1"
          currentPage={currentPage}
          edgePageCount={1}
          middlePagesSiblingCount={1}
          setCurrentPage={setCurrentPage}
          totalPages={totalPages}
          truncableText="..."
        >
          <Pagination.PrevButton
            className="flex h-8 w-8 items-center justify-center rounded-md border border-[#E5E7EB] text-[#6B7280] transition-colors hover:bg-[#F9FAFB] disabled:cursor-not-allowed disabled:opacity-40"
            disabled={currentPage === 0}
          >
            <FiChevronsLeft size={14} />
          </Pagination.PrevButton>

          <Pagination.PrevButton
            className="flex h-8 w-8 items-center justify-center rounded-md border border-[#E5E7EB] text-[#6B7280] transition-colors hover:bg-[#F9FAFB] disabled:cursor-not-allowed disabled:opacity-40"
            disabled={currentPage === 0}
          >
            <FiChevronLeft size={14} />
          </Pagination.PrevButton>

          <nav>
            <ul className="flex items-center gap-1">
              <Pagination.PageButton
                activeClassName="border-[#D1D5DB] bg-[#F3F4F6] text-[#111827]"
                className="flex h-8 min-w-8 items-center justify-center rounded-md border border-transparent px-2 text-xs font-medium text-[#6B7280] transition-colors hover:bg-[#F9FAFB]"
                inactiveClassName="text-[#6B7280]"
              />
            </ul>
          </nav>

          <Pagination.NextButton
            className="flex h-8 w-8 items-center justify-center rounded-md border border-[#E5E7EB] text-[#6B7280] transition-colors hover:bg-[#F9FAFB] disabled:cursor-not-allowed disabled:opacity-40"
            disabled={currentPage === totalPages - 1}
          >
            <FiChevronRight size={14} />
          </Pagination.NextButton>

          <Pagination.NextButton
            className="flex h-8 w-8 items-center justify-center rounded-md border border-[#E5E7EB] text-[#6B7280] transition-colors hover:bg-[#F9FAFB] disabled:cursor-not-allowed disabled:opacity-40"
            disabled={currentPage === totalPages - 1}
          >
            <FiChevronsRight size={14} />
          </Pagination.NextButton>
        </Pagination>
      </div>
    </div>
  )
}
